import { useMemo } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { useNavigate } from "react-router-dom";
import { 
  TrendingUp, 
  TrendingDown, 
  AlertTriangle, 
  Calculator,
  Activity,
  ArrowRight,
  DollarSign,
  Volume2,
  Zap,
  Bitcoin
} from "lucide-react";
import { formatUSD, formatPercent } from "@/lib/format";
import { useCryptoAssets } from "@/hooks/useCryptoAssets";
import { FearGreedHistoryChart } from "@/components/FearGreedHistoryChart";
import { StablecoinsCard } from "@/components/StablecoinsCard";
import { BTCDominanceChart } from "@/components/BTCDominanceChart";
import { useBTCDominanceLatest, getDominanceLabel, getDominanceColor } from "@/hooks/useBTCDominance";
import { cn } from "@/lib/utils";

const Overview = () => {
  const navigate = useNavigate();
  const { data: assets, isLoading, error } = useCryptoAssets();
  const { data: btcDominance, isLoading: domLoading } = useBTCDominanceLatest();

  const marketSummary = useMemo(() => {
    if (!assets?.length) return null;
    
    const gainers = assets.filter(asset => asset.change_24h > 0).length;
    const losers = assets.filter(asset => asset.change_24h < 0).length;
    const totalAlerts = assets.reduce((sum, asset) => sum + (asset.alerts?.length || 0), 0);
    const highAlerts = assets.reduce((sum, asset) => 
      sum + (asset.alerts?.filter(alert => alert.severity === 'high').length || 0), 0);
    const totalVolume = assets.reduce((sum, asset) => sum + asset.volume_24h, 0);
    const totalMarketCap = assets.reduce((sum, asset) => sum + asset.market_cap, 0);
    
    return { gainers, losers, totalAlerts, highAlerts, totalVolume, totalMarketCap };
  }, [assets]);

  const topGainers = useMemo(() => {
    if (!assets?.length) return [];
    return assets
      .filter(asset => asset.change_24h > 0)
      .sort((a, b) => b.change_24h - a.change_24h)
      .slice(0, 3);
  }, [assets]);

  const topLosers = useMemo(() => {
    if (!assets?.length) return [];
    return assets
      .filter(asset => asset.change_24h < 0)
      .sort((a, b) => a.change_24h - b.change_24h)
      .slice(0, 3);
  }, [assets]);

  const marketTrend = useMemo(() => {
    if (!marketSummary) return null;
    const ratio = marketSummary.gainers / (marketSummary.gainers + marketSummary.losers);
    if (ratio > 0.6) return { label: "Bullish", color: "text-profit", bg: "bg-profit/10" };
    if (ratio < 0.4) return { label: "Bearish", color: "text-destructive", bg: "bg-destructive/10" };
    return { label: "Neutral", color: "text-muted-foreground", bg: "bg-muted" };
  }, [marketSummary]);

  if (error) {
    return (
      <div className="flex items-center justify-center h-96">
        <Card className="p-6 max-w-md glass-card">
          <div className="text-center">
            <AlertTriangle className="mx-auto h-12 w-12 text-destructive mb-4" />
            <h2 className="text-xl font-semibold mb-2">Erro ao carregar dados</h2>
            <p className="text-muted-foreground">{error.message}</p>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Hero Section */}
      <div className="space-y-3 fade-in-up">
        <div className="flex items-center gap-4 flex-wrap">
          <h1 className="text-4xl font-bold tracking-tight text-gradient">
            Dashboard Overview
          </h1>
          {marketTrend && (
            <Badge className={cn("text-sm px-3 py-1", marketTrend.bg, marketTrend.color)}>
              <Zap className="h-3 w-3 mr-1" />
              {marketTrend.label}
            </Badge>
          )}
        </div>
        <p className="text-muted-foreground text-lg">
          Visão geral do mercado e suas ferramentas de análise
        </p>
        <div className="gradient-line w-24 mt-2" />
      </div>

      {/* Market Summary Cards */}
      {isLoading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {Array.from({ length: 4 }).map((_, i) => (
            <Card key={i} className="glass-card shimmer">
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <Skeleton className="h-4 w-20" />
                <Skeleton className="h-4 w-4" />
              </CardHeader>
              <CardContent>
                <Skeleton className="h-8 w-16 mb-2" />
                <Skeleton className="h-3 w-24" />
              </CardContent>
            </Card>
          ))}
        </div>
      ) : marketSummary ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <Card className="glass-card hover-scale fade-in-up delay-100 group">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Market Cap</CardTitle>
              <div className="h-8 w-8 rounded-lg bg-crypto-green/10 flex items-center justify-center group-hover:scale-110 transition-transform">
                <DollarSign className="h-4 w-4 text-crypto-green" />
              </div>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{formatUSD(marketSummary.totalMarketCap)}</div>
              <p className="text-xs text-muted-foreground mt-1">Total do mercado</p>
            </CardContent>
          </Card>

          <Card className="glass-card hover-scale fade-in-up delay-200 group">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Volume 24h</CardTitle>
              <div className="h-8 w-8 rounded-lg bg-primary/10 flex items-center justify-center group-hover:scale-110 transition-transform">
                <Volume2 className="h-4 w-4 text-primary" />
              </div>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{formatUSD(marketSummary.totalVolume)}</div>
              <p className="text-xs text-muted-foreground mt-1">Volume de negociação</p>
            </CardContent>
          </Card>

          <Card className="glass-card hover-scale fade-in-up delay-300 group">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">BTC Dominance</CardTitle>
              <div 
                className="h-8 w-8 rounded-lg flex items-center justify-center group-hover:scale-110 transition-transform"
                style={{ backgroundColor: btcDominance ? `${getDominanceColor(Number(btcDominance.btc_dominance))}20` : 'hsl(var(--muted))' }}
              >
                <Bitcoin 
                  className="h-4 w-4" 
                  style={{ color: btcDominance ? getDominanceColor(Number(btcDominance.btc_dominance)) : 'hsl(var(--muted-foreground))' }}
                />
              </div>
            </CardHeader>
            <CardContent>
              {domLoading ? (
                <Skeleton className="h-8 w-20" />
              ) : btcDominance ? (
                <>
                  <div className="text-2xl font-bold">{Number(btcDominance.btc_dominance).toFixed(1)}%</div>
                  <Badge 
                    variant="outline" 
                    className="text-xs mt-1"
                    style={{ 
                      borderColor: getDominanceColor(Number(btcDominance.btc_dominance)),
                      color: getDominanceColor(Number(btcDominance.btc_dominance))
                    }}
                  >
                    {getDominanceLabel(Number(btcDominance.btc_dominance))}
                  </Badge>
                </>
              ) : (
                <p className="text-xs text-muted-foreground">Collecting data...</p>
              )}
            </CardContent>
          </Card>

          <div className="fade-in-up delay-400 hover-scale">
            <StablecoinsCard compact />
          </div>
        </div>
      ) : null}

      {/* Fear & Greed, BTC Dominance and Top Movers */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Fear & Greed with History */}
        <Card className="glass-card fade-in-up delay-500">
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2">
              <div className="h-8 w-8 rounded-lg bg-primary/10 flex items-center justify-center">
                <Activity className="h-4 w-4 text-primary" />
              </div>
              Fear & Greed Index
            </CardTitle>
          </CardHeader>
          <CardContent>
            <FearGreedHistoryChart days={90} height={200} />
          </CardContent>
        </Card>

        {/* BTC Dominance Chart */}
        <Card className="glass-card fade-in-up delay-550">
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2">
              <div 
                className="h-8 w-8 rounded-lg flex items-center justify-center"
                style={{ backgroundColor: 'hsl(38, 92%, 50%, 0.1)' }}
              >
                <Bitcoin className="h-4 w-4" style={{ color: 'hsl(38, 92%, 50%)' }} />
              </div>
              BTC Dominance
            </CardTitle>
          </CardHeader>
          <CardContent>
            <BTCDominanceChart days={90} height={200} />
          </CardContent>
        </Card>
      </div>

      {/* Top Movers */}
      <div className="grid grid-cols-1 gap-6">
        <Card className="glass-card fade-in-up delay-600">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <div className="h-8 w-8 rounded-lg bg-profit/10 flex items-center justify-center">
                <TrendingUp className="h-4 w-4 text-profit" />
              </div>
              Top Movers 24h
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 gap-4">
              {/* Top Gainers */}
              <div className="space-y-2">
                <h4 className="text-sm font-medium text-profit flex items-center gap-2 mb-3">
                  <TrendingUp className="h-4 w-4" /> Gainers
                </h4>
                <div className="space-y-2">
                  {topGainers.map((asset, i) => (
                    <div 
                      key={asset.id} 
                      onClick={() => navigate(`/app/asset/${asset.id}`)}
                      className={cn(
                        "flex items-center gap-3 p-2.5 rounded-lg cursor-pointer transition-all duration-200",
                        "hover:bg-profit/5 hover:scale-[1.02]",
                        i === 0 && "bg-profit/5 border border-profit/20"
                      )}
                    >
                      {asset.image && (
                        <img 
                          src={asset.image} 
                          alt={asset.name} 
                          className="w-8 h-8 rounded-full ring-2 ring-profit/20" 
                        />
                      )}
                      <div className="flex-1 min-w-0">
                        <span className="text-sm font-semibold block truncate">{asset.symbol}</span>
                        <span className="text-xs text-muted-foreground truncate block">{asset.name}</span>
                      </div>
                      <Badge className="bg-profit/10 text-profit border-profit/20 shrink-0">
                        {formatPercent(asset.change_24h)}
                      </Badge>
                    </div>
                  ))}
                </div>
              </div>
              
              {/* Separator */}
              <div className="hidden lg:block absolute left-1/2 top-16 bottom-4 w-px bg-gradient-to-b from-transparent via-border to-transparent" />
              
              {/* Top Losers */}
              <div className="space-y-2">
                <h4 className="text-sm font-medium text-destructive flex items-center gap-2 mb-3">
                  <TrendingDown className="h-4 w-4" /> Losers
                </h4>
                <div className="space-y-2">
                  {topLosers.map((asset, i) => (
                    <div 
                      key={asset.id} 
                      onClick={() => navigate(`/app/asset/${asset.id}`)}
                      className={cn(
                        "flex items-center gap-3 p-2.5 rounded-lg cursor-pointer transition-all duration-200",
                        "hover:bg-destructive/5 hover:scale-[1.02]",
                        i === 0 && "bg-destructive/5 border border-destructive/20"
                      )}
                    >
                      {asset.image && (
                        <img 
                          src={asset.image} 
                          alt={asset.name} 
                          className="w-8 h-8 rounded-full ring-2 ring-destructive/20" 
                        />
                      )}
                      <div className="flex-1 min-w-0">
                        <span className="text-sm font-semibold block truncate">{asset.symbol}</span>
                        <span className="text-xs text-muted-foreground truncate block">{asset.name}</span>
                      </div>
                      <Badge variant="destructive" className="bg-destructive/10 shrink-0">
                        {formatPercent(asset.change_24h)}
                      </Badge>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Quick Actions - Modernized */}
      <div className="fade-in-up delay-700">
        <div className="mb-4">
          <h2 className="text-xl font-semibold">Ferramentas Disponíveis</h2>
          <p className="text-sm text-muted-foreground">
            Acesse rapidamente as principais funcionalidades da plataforma
          </p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div 
            onClick={() => navigate('/app/markets')}
            className="group glass-card p-6 cursor-pointer hover:border-crypto-green/30 transition-all duration-300"
          >
            <div className="h-12 w-12 rounded-xl bg-crypto-green/10 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300">
              <TrendingUp className="h-6 w-6 text-crypto-green" />
            </div>
            <h3 className="font-semibold mb-1">Markets</h3>
            <p className="text-sm text-muted-foreground mb-3">
              Análise completa de preços, volume e métricas de todos os ativos
            </p>
            <div className="flex items-center text-sm text-muted-foreground group-hover:text-primary transition-colors">
              <span>Explorar</span>
              <ArrowRight className="h-4 w-4 ml-1 group-hover:translate-x-1 transition-transform" />
            </div>
          </div>

          <div 
            onClick={() => navigate('/app/valuation')}
            className="group glass-card p-6 cursor-pointer hover:border-primary/30 transition-all duration-300"
          >
            <div className="h-12 w-12 rounded-xl bg-primary/10 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300">
              <Calculator className="h-6 w-6 text-primary" />
            </div>
            <h3 className="font-semibold mb-1">Valuation</h3>
            <p className="text-sm text-muted-foreground mb-3">
              Avaliação DCF e métricas fundamentalistas para análise de valor
            </p>
            <div className="flex items-center text-sm text-muted-foreground group-hover:text-primary transition-colors">
              <span>Calcular</span>
              <ArrowRight className="h-4 w-4 ml-1 group-hover:translate-x-1 transition-transform" />
            </div>
          </div>

          <div 
            onClick={() => navigate('/app/alpha-onchain')}
            className="group glass-card p-6 cursor-pointer hover:border-crypto-purple/30 transition-all duration-300"
          >
            <div className="h-12 w-12 rounded-xl bg-crypto-purple/10 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300">
              <Activity className="h-6 w-6 text-crypto-purple" />
            </div>
            <h3 className="font-semibold mb-1">Alpha On-Chain</h3>
            <p className="text-sm text-muted-foreground mb-3">
              Dados on-chain avançados, fluxos de exchanges e smart money
            </p>
            <div className="flex items-center text-sm text-muted-foreground group-hover:text-primary transition-colors">
              <span>Analisar</span>
              <ArrowRight className="h-4 w-4 ml-1 group-hover:translate-x-1 transition-transform" />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Overview;
